
import { computed, defineComponent, ref, toRef, watch } from 'vue';

const DEFAULT_COLOR_CHECKED = '#75c791';
const DEFAULT_COLOR_UNCHECKED = '#bfcbd9';
const DEFAULT_LABEL_CHECKED = 'on';
const DEFAULT_LABEL_UNCHECKED = 'off';
const DEFAULT_SWITCH_COLOR = '#fff';

export default defineComponent({
    name: 'ToggleButton',
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        name: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        tag: {
            type: String,
            default: 'div',
        },
        speed: {
            type: Number,
            default: 300,
        },
        color: {
            type: [String, Object],
            validator(value) {
                return (
                    isString(value) ||
                    has(value, 'checked') ||
                    has(value, 'unchecked') ||
                    has(value, 'disabled')
                );
            },
            default: '',
        },
        switchColor: {
            type: [String, Object],
            validator(value) {
                return (
                    isString(value) || has(value, 'checked') || has(value, 'unchecked')
                );
            },
            default: '',
        },
        cssColors: {
            type: Boolean,
            default: false,
        },
        labels: {
            type: [Boolean, Object],
            default: false,
            validator(value) {
                return typeof value === 'object'
                    ? value.checked || value.unchecked
                    : typeof value === 'boolean';
            },
        },
        height: {
            type: Number,
            default: 22,
        },
        width: {
            type: Number,
            default: 50,
        },
        margin: {
            type: Number,
            default: 3,
        },
        fontSize: {
            type: Number,
            default: 16,
        },
    },
    emits: ['update:modelValue', 'change'],
    setup(props, { emit }) {
        const modelValue = toRef(props, 'modelValue');
        const toggled = ref(!!modelValue.value);

        const className = computed(() => {
            return [
                'vue-js-switch',
                {
                    toggled: toggled.value,
                    disabled: props.disabled,
                },
            ];
        });

        const colorChecked = computed(() => {
            if (!isObject(props.color)) {
                return props.color || DEFAULT_COLOR_CHECKED;
            }

            return get(props.color, 'checked', DEFAULT_COLOR_CHECKED);
        });

        const colorUnchecked = computed(() => {
            return get(props.color, 'unchecked', DEFAULT_COLOR_UNCHECKED);
        });

        const colorCurrent = computed(() => {
            return toggled.value ? colorChecked.value : colorUnchecked.value;
        });

        const colorDisabled = computed(() => {
            return get(props.color, 'disabled', colorCurrent.value);
        });

        const coreStyle = computed(() => {
            return {
                width: px(props.width),
                height: px(props.height),
                backgroundColor: props.cssColors
                    ? null
                    : props.disabled
                        ? colorDisabled.value
                        : colorCurrent.value,
                borderRadius: px(Math.round(props.height / 2)),
            };
        });

        const buttonRadius = computed(() => {
            return props.height - props.margin * 2;
        });

        const distance = computed(() => {
            return px(props.width - props.height + props.margin);
        });

        const switchColorChecked = computed(() => {
            return get(props.switchColor, 'checked', DEFAULT_SWITCH_COLOR);
        });

        const switchColorUnchecked = computed(() => {
            return get(props.switchColor, 'unchecked', DEFAULT_SWITCH_COLOR);
        });

        const switchColorCurrent = computed(() => {
            if (!isObject(props.switchColor)) {
                return props.switchColor || DEFAULT_SWITCH_COLOR;
            }

            return toggled.value ? switchColorChecked.value : switchColorUnchecked.value;
        });

        const buttonStyle = computed(() => {
            const transition = `transform ${props.speed}ms`;
            const margin = px(props.margin);

            const transform = toggled.value
                ? translate(distance.value, margin)
                : translate(margin, margin);

            const background = props.switchColor ? switchColorCurrent.value : null;

            return {
                width: px(buttonRadius.value),
                height: px(buttonRadius.value),
                transition,
                transform,
                background,
            };
        });

        const labelStyle = computed(() => {
            return {
                lineHeight: px(props.height),
                fontSize: props.fontSize ? px(props.fontSize) : null,
            };
        });

        const labelChecked = computed(() => {
            return get(props.labels, 'checked', DEFAULT_LABEL_CHECKED);
        });

        const labelUnchecked = computed(() => {
            return get(props.labels, 'unchecked', DEFAULT_LABEL_UNCHECKED);
        });

        const toggle = (event) => {
            toggled.value = !toggled.value;

            emit('update:modelValue', toggled.value);
            emit('change', {
                value: toggled.value,
                tag: props.tag,
                srcEvent: event,
            });
        };

        const keyToggle = (event) => {
            // the key event happens whether the control is disabled or not
            // nothing should be done if disabled is true
            if (props.disabled) {
                return;
            }

            toggle(event);
        };

        watch(modelValue, (newValue) => {
            toggled.value = !!newValue;
        });

        return {
            className,
            coreStyle,
            buttonStyle,
            labelStyle,
            toggled,
            labelChecked,
            labelUnchecked,
            toggle,
            keyToggle,
        };
    },
});

function isString(value) {
    return typeof value === 'string';
}

function isObject(value) {
    return typeof value === 'object';
}

function has(object, key) {
    /* eslint-disable */
    return isObject(object) && object.hasOwnProperty(key);
    /* eslint-enable */
}

function get(object, key, defaultValue) {
    return has(object, key) ? object[key] : defaultValue;
}

function px(value) {
    return `${value}px`;
}

function translate(x, y) {
    return `translate(${x}, ${y})`;
}
